import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { fetchCategory } from '@/fetchers/categories';
import { ParsedUrlQuery } from 'node:querystring';
import { ICategory } from '@/models';

import MyHead from '@/components/MyHead';

import BreadCrumbs from '@/components/v2/BreadCrumbs';
import Heading from '@/components/v2/Pages/Category/Heading';
import { DesktopFilters } from '@/components/v2/Pages/Category/Filters';
import Products from '@/components/v2/Pages/Category/Products';

import Description from '@/components/v2/Pages/Category/Description';

import { useProducts } from '@/hooks/products/use-products';
import {
  SUPPORTED_LANG,
  getLangCountry,
  getRealLangCountry
} from '@/helpers/languages';
import { SUPPORTED_COUNTRY } from '@/helpers/countries';
import { fetchAlternateUrls } from '@/fetchers/commons';
import { AlternateLangs } from '@/models/app';

export default function CategoryPage({
  category,
  alternates
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { data: { count = 0, products = [] } = {}, isLoading } = useProducts({
    category
  });

  return (
    <>
      <MyHead description={category.seoText} alternates={alternates} />

      <BreadCrumbs
        data={category.breadcrumbs}
        containerClassName="hidden section py-6 md:flex"
      />

      <Heading category={category} count={count} />

      <section className="section grid xl:grid-cols-[250px_1fr] xl:gap-x-16">
        <DesktopFilters category={category} />
        <Products
          category={category}
          products={products}
          isLoading={isLoading}
        />
      </section>

      <Description category={category} />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

interface Params extends ParsedUrlQuery {
  categories: string[];
}

export const getStaticProps: GetStaticProps<{
  category: ICategory;
  alternates: AlternateLangs[];
}> = async ctx => {
  const { categories } = ctx.params as Params;

  const real = getRealLangCountry(ctx.params);

  if (
    !SUPPORTED_LANG.includes(real.lang) ||
    !SUPPORTED_COUNTRY.includes(real.country)
  ) {
    return { notFound: true, revalidate: 60 };
  }

  const slug = categories.filter(Boolean).join('/');

  const [category, alternates] = await Promise.all([
    fetchCategory({
      ...getLangCountry(ctx.params),
      slug
    }),
    fetchAlternateUrls({
      url: slug,
      ...getLangCountry(ctx.params),
      prefix: '/shop'
    })
  ]);

  if (!category) {
    return { notFound: true, revalidate: 60 };
  }

  return {
    props: {
      alternates,
      category
    },
    revalidate: 86_400
  };
};
